"use client";
import * as React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import resolveProps from "@mui/utils/resolveProps";
import composeClasses from "@mui/utils/composeClasses";
import { unstable_useId as useId } from "../utils";
import rootShouldForwardProp from "../styles/rootShouldForwardProp";
import { styled } from "../zero-styled";
import memoTheme from "../utils/memoTheme";
import { useDefaultProps } from "../DefaultPropsProvider";
import ButtonBase from "../ButtonBase";
import CircularProgress from "../CircularProgress";
import capitalize from "../utils/capitalize";
import createSimplePaletteValueFilter from "../utils/createSimplePaletteValueFilter";
import buttonClasses, { getButtonUtilityClass } from "./buttonClasses";
import ButtonGroupContext from "../ButtonGroup/ButtonGroupContext";
import ButtonGroupButtonContext from "../ButtonGroup/ButtonGroupButtonContext";
import { getTransitionStyles } from "../transitions/utils";

const useUtilityClasses = (ownerState) => {
  const { color, disableElevation, fullWidth, size, variant, loading, loadingPosition, classes } =
    ownerState;

  const slots = {
    root: [
      "root",
      loading && "loading",
      variant,
      `size${capitalize(size)}`,
      `color${capitalize(color)}`,
      disableElevation && "disableElevation",
      fullWidth && "fullWidth",
      loading && `loadingPosition${capitalize(loadingPosition)}`,
    ],
    startIcon: ["icon", "startIcon"],
    endIcon: ["icon", "endIcon"],
    loadingIndicator: ["loadingIndicator"],
    loadingWrapper: ["loadingWrapper"],
  };

  const composedClasses = composeClasses(slots, getButtonUtilityClass, classes);

  return {
    ...classes, // forward the focused, disabled, etc. classes to the ButtonBase
    ...composedClasses,
  };
};

const commonIconStyles = [
  {
    props: { size: "small" },
    style: {
      "& > *:nth-of-type(1)": {
        fontSize: 18,
      },
    },
  },
  {
    props: { size: "medium" },
    style: {
      "& > *:nth-of-type(1)": {
        fontSize: 20,
      },
    },
  },
  {
    props: { size: "large" },
    style: {
      "& > *:nth-of-type(1)": {
        fontSize: 22,
      },
    },
  },
];

// Built-in sizes route padding via variants; any other size routes inline.
const buttonSizes = ["small", "medium", "large"];

// Color/state adapter (docs/adr/0002). Each variant-prop is consumed through the
// pre-existing `--variant-*` seam over a private `--_<variant><Prop>` default.
// Two kinds of state:
//   • value-state (rest / hover / disabled) — genuinely sets the prop. Its public
//     token lives INSIDE the default: `--_<vp>: var(<token>, <palette>)`, and the
//     seam reads `var(--_<vp>)`. So a rest/hover override is captured in the default.
//   • inert-state (focus / active) — Button moves only box-shadow. It routes its
//     token in the SEAM over the default: `--variant-<vp>: var(<token>, var(--_<vp>))`.
//     Unset, it inherits whatever the default currently resolves to (incl. a rest or
//     hover override) — never the bare palette literal.
// Unset, every token falls back to today's palette value (color-identical). `rest`
// omits the `<state>` segment; `focus` maps to `.Mui-focusVisible`.
const buildButtonColorVars = (theme, color) => {
  const vars = theme.vars || theme;
  const main = vars.palette[color].main;
  const dark = vars.palette[color].dark;
  const contrastText = vars.palette[color].contrastText;
  const hoverBg = theme.alpha(main, vars.palette.action.hoverOpacity);
  const outlinedBorderRest = theme.alpha(main, 0.5);
  const disabledBg = vars.palette.action.disabledBackground;
  const disabledFg = vars.palette.action.disabled;

  // One entry per variant-prop: its seam suffix, variant, prop key, and the palette
  // literal each value-state resolves to today (`hover`/`disabled` omitted = inert there).
  const props = [
    {
      vp: "containedBg",
      variant: "contained",
      prop: "bg",
      rest: main,
      hover: dark,
      disabled: disabledBg,
    },
    {
      vp: "containedColor",
      variant: "contained",
      prop: "fg",
      rest: contrastText,
      disabled: disabledFg,
    },
    { vp: "textColor", variant: "text", prop: "fg", rest: main, disabled: disabledFg },
    { vp: "textBg", variant: "text", prop: "bg", rest: "transparent", hover: hoverBg },
    { vp: "outlinedColor", variant: "outlined", prop: "fg", rest: main, disabled: disabledFg },
    {
      vp: "outlinedBorder",
      variant: "outlined",
      prop: "border",
      rest: outlinedBorderRest,
      hover: main,
      disabled: disabledBg,
    },
    { vp: "outlinedBg", variant: "outlined", prop: "bg", rest: "transparent", hover: hoverBg },
  ];
  const token = (variant, state, prop) =>
    `--Button-${variant}-${color}${state ? `-${state}` : ""}-${prop}`;

  // Build a state's style. `valueKey` names the palette field for value-states
  // (rest = the literal directly); null marks an inert state (all props routed).
  const block = (state, valueKey) => {
    const style = {};
    props.forEach((p) => {
      const value = valueKey === "rest" ? p.rest : p[valueKey];
      if (value !== undefined) {
        // value-state: token inside the default, seam just reads it
        style[`--_${p.vp}`] = `var(${token(p.variant, state, p.prop)}, ${value})`;
        style[`--variant-${p.vp}`] = `var(--_${p.vp})`;
      } else {
        // inert for this state: route the token over the current default
        style[`--variant-${p.vp}`] = `var(${token(p.variant, state, p.prop)}, var(--_${p.vp}))`;
      }
    });
    return style;
  };

  return {
    ...block("", "rest"),
    "@media (hover: hover)": { "&:hover": block("hover", "hover") },
    [`&.${buttonClasses.focusVisible}`]: block("focus", null),
    "&:active": block("active", null),
    // disabled: the value-states advance to today's neutral greys (color-invariant);
    // replaces the literal disabled rules removed from root/contained/outlined.
    [`&.${buttonClasses.disabled}`]: block("disabled", "disabled"),
  };
};

const ButtonRoot = styled(ButtonBase, {
  shouldForwardProp: (prop) => rootShouldForwardProp(prop) || prop === "classes",
  name: "MuiButton",
  slot: "Root",
  overridesResolver: (props, styles) => {
    const { ownerState } = props;

    return [
      styles.root,
      styles[ownerState.variant],
      styles[`size${capitalize(ownerState.size)}`],
      ownerState.color === "inherit" && styles.colorInherit,
      ownerState.disableElevation && styles.disableElevation,
      ownerState.fullWidth && styles.fullWidth,
      ownerState.loading && styles.loading,
    ];
  },
})(
  memoTheme(({ theme }) => {
    const inheritContainedBackgroundColor =
      theme.palette.mode === "light" ? theme.palette.grey[300] : theme.palette.grey[800];

    const inheritContainedHoverBackgroundColor =
      theme.palette.mode === "light" ? theme.palette.grey.A100 : theme.palette.grey[700];
    return {
      ...theme.typography.button,
      // Agnostic layer: the only spacing surface the styled root reads. `--_pad`
      // is the universal default (today's root padding); variants specialize it
      // per (variant, size), so a custom variant/size still gets a sane value.
      "--_pad": "6px 16px",
      padding: "var(--Button-pad, var(--_pad))",
      minWidth: 64,
      border: 0,
      borderRadius: (theme.vars || theme).shape.borderRadius,
      ...getTransitionStyles(theme, ["background-color", "box-shadow", "border-color", "color"], {
        duration: theme.transitions.duration.short,
      }),
      "&:hover": {
        textDecoration: "none",
      },
      variants: [
        // Built-in size routing (CSS, deduped) — exposes the public sized token
        // over the internal default. Custom sizes are routed inline instead.
        {
          props: { size: "small" },
          style: { "--Button-pad": "var(--Button-small-pad, var(--_pad))" },
        },
        {
          props: { size: "medium" },
          style: { "--Button-pad": "var(--Button-medium-pad, var(--_pad))" },
        },
        {
          props: { size: "large" },
          style: { "--Button-pad": "var(--Button-large-pad, var(--_pad))" },
        },
        {
          props: { variant: "contained" },
          style: {
            "--_pad": "6px 16px", // medium default; small/large override below
            color: `var(--variant-containedColor)`,
            backgroundColor: `var(--variant-containedBg)`,
            boxShadow: (theme.vars || theme).shadows[2],
            "&:hover": {
              boxShadow: (theme.vars || theme).shadows[4],
              // Reset on touch devices, it doesn't add specificity
              "@media (hover: none)": {
                boxShadow: (theme.vars || theme).shadows[2],
              },
            },
            "&:active": {
              boxShadow: (theme.vars || theme).shadows[8],
            },
            [`&.${buttonClasses.focusVisible}`]: {
              boxShadow: (theme.vars || theme).shadows[6],
            },
            [`&.${buttonClasses.disabled}`]: {
              boxShadow: (theme.vars || theme).shadows[0],
            },
          },
        },
        {
          props: { variant: "outlined" },
          style: {
            "--_pad": "5px 15px", // medium default; small/large override below
            border: "1px solid currentColor",
            borderColor: `var(--variant-outlinedBorder, currentColor)`,
            backgroundColor: `var(--variant-outlinedBg)`,
            color: `var(--variant-outlinedColor)`,
          },
        },
        {
          props: { variant: "text" },
          style: {
            "--_pad": "6px 8px", // medium default; small/large override below
            color: `var(--variant-textColor)`,
            backgroundColor: `var(--variant-textBg)`,
          },
        },
        ...Object.entries(theme.palette)
          .filter(createSimplePaletteValueFilter())
          .map(([color]) => ({
            props: { color },
            style: buildButtonColorVars(theme, color),
          })),
        {
          props: {
            color: "inherit",
          },
          style: {
            color: "inherit",
            borderColor: "currentColor",
            "--variant-containedBg": theme.vars
              ? theme.vars.palette.Button.inheritContainedBg
              : inheritContainedBackgroundColor,
            "@media (hover: hover)": {
              "&:hover": {
                "--variant-containedBg": theme.vars
                  ? theme.vars.palette.Button.inheritContainedHoverBg
                  : inheritContainedHoverBackgroundColor,
                "--variant-textBg": theme.alpha(
                  (theme.vars || theme).palette.text.primary,
                  (theme.vars || theme).palette.action.hoverOpacity
                ),
                "--variant-outlinedBg": theme.alpha(
                  (theme.vars || theme).palette.text.primary,
                  (theme.vars || theme).palette.action.hoverOpacity
                ),
              },
            },
            // inherit isn't routed through buildButtonColorVars, so restore the
            // disabled greys the removed literal rules used to provide.
            [`&.${buttonClasses.disabled}`]: {
              color: (theme.vars || theme).palette.action.disabled,
              borderColor: (theme.vars || theme).palette.action.disabledBackground,
              "--variant-containedBg": (theme.vars || theme).palette.action.disabledBackground,
            },
          },
        },
        {
          props: {
            size: "small",
            variant: "text",
          },
          style: {
            "--_pad": "4px 5px",
            fontSize: theme.typography.pxToRem(13),
          },
        },
        {
          props: {
            size: "large",
            variant: "text",
          },
          style: {
            "--_pad": "8px 11px",
            fontSize: theme.typography.pxToRem(15),
          },
        },
        {
          props: {
            size: "small",
            variant: "outlined",
          },
          style: {
            "--_pad": "3px 9px",
            fontSize: theme.typography.pxToRem(13),
          },
        },
        {
          props: {
            size: "large",
            variant: "outlined",
          },
          style: {
            "--_pad": "7px 21px",
            fontSize: theme.typography.pxToRem(15),
          },
        },
        {
          props: {
            size: "small",
            variant: "contained",
          },
          style: {
            "--_pad": "4px 10px",
            fontSize: theme.typography.pxToRem(13),
          },
        },
        {
          props: {
            size: "large",
            variant: "contained",
          },
          style: {
            "--_pad": "8px 22px",
            fontSize: theme.typography.pxToRem(15),
          },
        },
        {
          props: {
            disableElevation: true,
          },
          style: {
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
            [`&.${buttonClasses.focusVisible}`]: {
              boxShadow: "none",
            },
            "&:active": {
              boxShadow: "none",
            },
            [`&.${buttonClasses.disabled}`]: {
              boxShadow: "none",
            },
          },
        },
        {
          props: { fullWidth: true },
          style: { width: "100%" },
        },
        {
          props: {
            loadingPosition: "center",
          },
          style: {
            ...getTransitionStyles(theme, ["background-color", "box-shadow", "border-color"], {
              duration: theme.transitions.duration.short,
            }),
            [`&.${buttonClasses.loading}`]: {
              color: "transparent",
            },
          },
        },
      ],
    };
  })
);

const ButtonStartIcon = styled("span", {
  name: "MuiButton",
  slot: "StartIcon",
  overridesResolver: (props, styles) => {
    const { ownerState } = props;

    return [styles.startIcon, ownerState.loading && styles.startIconLoadingStart];
  },
})(({ theme }) => ({
  display: "inherit",
  alignItems: "center",
  marginRight: 8,
  marginLeft: -4,
  "&::before": {
    content: '"\\200b"',
    width: 0,
    overflow: "hidden",
  },
  variants: [
    {
      props: { size: "small" },
      style: {
        marginLeft: -2,
      },
    },
    {
      props: { loadingPosition: "start", loading: true },
      style: {
        ...getTransitionStyles(theme, ["opacity"], {
          duration: theme.transitions.duration.short,
        }),
        opacity: 0,
      },
    },
    {
      props: { loadingPosition: "start", loading: true, fullWidth: true },
      style: {
        marginRight: -8,
      },
    },
    ...commonIconStyles,
  ],
}));

const ButtonEndIcon = styled("span", {
  name: "MuiButton",
  slot: "EndIcon",
  overridesResolver: (props, styles) => {
    const { ownerState } = props;

    return [styles.endIcon, ownerState.loading && styles.endIconLoadingEnd];
  },
})(({ theme }) => ({
  display: "inherit",
  marginRight: -4,
  marginLeft: 8,
  variants: [
    {
      props: { size: "small" },
      style: {
        marginRight: -2,
      },
    },
    {
      props: { loadingPosition: "end", loading: true },
      style: {
        ...getTransitionStyles(theme, ["opacity"], {
          duration: theme.transitions.duration.short,
        }),
        opacity: 0,
      },
    },
    {
      props: { loadingPosition: "end", loading: true, fullWidth: true },
      style: {
        marginLeft: -8,
      },
    },
    ...commonIconStyles,
  ],
}));

const ButtonLoadingIndicator = styled("span", {
  name: "MuiButton",
  slot: "LoadingIndicator",
})(({ theme }) => ({
  display: "none",
  position: "absolute",
  visibility: "visible",
  variants: [
    { props: { loading: true }, style: { display: "flex" } },
    {
      props: { loadingPosition: "start" },
      style: {
        left: 14,
      },
    },
    {
      props: {
        loadingPosition: "start",
        size: "small",
      },
      style: {
        left: 10,
      },
    },
    {
      props: {
        variant: "text",
        loadingPosition: "start",
      },
      style: {
        left: 6,
      },
    },
    {
      props: {
        loadingPosition: "center",
      },
      style: {
        left: "50%",
        transform: "translate(-50%)",
        color: (theme.vars || theme).palette.action.disabled,
      },
    },
    {
      props: { loadingPosition: "end" },
      style: {
        right: 14,
      },
    },
    {
      props: {
        loadingPosition: "end",
        size: "small",
      },
      style: {
        right: 10,
      },
    },
    {
      props: {
        variant: "text",
        loadingPosition: "end",
      },
      style: {
        right: 6,
      },
    },
    {
      props: { loadingPosition: "start", fullWidth: true },
      style: {
        position: "relative",
        left: -10,
      },
    },
    {
      props: { loadingPosition: "end", fullWidth: true },
      style: {
        position: "relative",
        right: -10,
      },
    },
  ],
}));

const ButtonLoadingIconPlaceholder = styled("span", {
  name: "MuiButton",
  slot: "LoadingIconPlaceholder",
})({
  display: "inline-block",
  width: "1em",
  height: "1em",
});

const Button = React.forwardRef(function Button(inProps, ref) {
  // props priority: `inProps` > `contextProps` > `themeDefaultProps`
  const contextProps = React.useContext(ButtonGroupContext);
  const buttonGroupButtonContextPositionClassName = React.useContext(ButtonGroupButtonContext);
  const resolvedProps = resolveProps(contextProps, inProps);
  const props = useDefaultProps({ props: resolvedProps, name: "MuiButton" });
  const {
    children,
    color = "primary",
    component = "button",
    className,
    disabled = false,
    disableElevation = false,
    disableFocusRipple = false,
    endIcon: endIconProp,
    focusVisibleClassName,
    fullWidth = false,
    id: idProp,
    loading = null,
    loadingIndicator: loadingIndicatorProp,
    loadingPosition = "center",
    size = "medium",
    startIcon: startIconProp,
    type,
    variant = "text",
    ...other
  } = props;

  const loadingId = useId(idProp);
  const loadingIndicator = loadingIndicatorProp ?? (
    <CircularProgress aria-labelledby={loadingId} color="inherit" size={16} />
  );

  const ownerState = {
    ...props,
    color,
    component,
    disabled,
    disableElevation,
    disableFocusRipple,
    fullWidth,
    loading,
    loadingIndicator,
    loadingPosition,
    size,
    type,
    variant,
  };

  const classes = useUtilityClasses(ownerState);

  // Material UI layer: built-in sizes route the public sized token via variants
  // (deduped CSS). A custom size has no such variant, so route it inline — the
  // token name carries the runtime size string, keeping custom sizes tunable for
  // free. The `--_pad` default lives in the variants. See docs/adr/0001.
  const densityVars = buttonSizes.includes(size)
    ? undefined
    : { "--Button-pad": `var(--Button-${size}-pad, var(--_pad))` };

  const startIcon = (startIconProp || (loading && loadingPosition === "start")) && (
    <ButtonStartIcon className={classes.startIcon} ownerState={ownerState}>
      {startIconProp || (
        <ButtonLoadingIconPlaceholder
          className={classes.loadingIconPlaceholder}
          ownerState={ownerState}
        />
      )}
    </ButtonStartIcon>
  );

  const endIcon = (endIconProp || (loading && loadingPosition === "end")) && (
    <ButtonEndIcon className={classes.endIcon} ownerState={ownerState}>
      {endIconProp || (
        <ButtonLoadingIconPlaceholder
          className={classes.loadingIconPlaceholder}
          ownerState={ownerState}
        />
      )}
    </ButtonEndIcon>
  );

  const positionClassName = buttonGroupButtonContextPositionClassName || "";

  const loader =
    typeof loading === "boolean" ? (
      // use plain HTML span to minimize the runtime overhead
      <span className={classes.loadingWrapper} style={{ display: "contents" }}>
        {loading && (
          <ButtonLoadingIndicator className={classes.loadingIndicator} ownerState={ownerState}>
            {loadingIndicator}
          </ButtonLoadingIndicator>
        )}
      </span>
    ) : null;

  // Don't forward the 'root' classes to the ButtonBase, as they will get duplicated with the one passed to the className prop.
  const { root, ...forwardedClasses } = classes;

  return (
    <ButtonRoot
      ownerState={ownerState}
      className={clsx(contextProps.className, classes.root, className, positionClassName)}
      component={component}
      disabled={disabled || loading}
      focusRipple={!disableFocusRipple}
      focusVisibleClassName={clsx(classes.focusVisible, focusVisibleClassName)}
      ref={ref}
      internalNativeButton
      type={type}
      id={loading ? loadingId : idProp}
      {...other}
      style={{ ...densityVars, ...other.style }}
      classes={forwardedClasses}
    >
      {startIcon}
      {loadingPosition !== "end" && loader}
      {children}
      {loadingPosition === "end" && loader}
      {endIcon}
    </ButtonRoot>
  );
});

Button.propTypes /* remove-proptypes */ = {
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │    To update them, edit the d.ts file and run `pnpm proptypes`.     │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * @ignore
   */
  className: PropTypes.string,
  /**
   * The color of the component.
   * It supports both default and custom theme colors, which can be added as shown in the
   * [palette customization guide](https://mui.com/material-ui/customization/palette/#custom-colors).
   * @default 'primary'
   */
  color: PropTypes /* @typescript-to-proptypes-ignore */.oneOfType([
    PropTypes.oneOf(["inherit", "primary", "secondary", "success", "error", "info", "warning"]),
    PropTypes.string,
  ]),
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   */
  component: PropTypes.elementType,
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, no elevation is used.
   * @default false
   */
  disableElevation: PropTypes.bool,
  /**
   * If `true`, the  keyboard focus ripple is disabled.
   * @default false
   */
  disableFocusRipple: PropTypes.bool,
  /**
   * If `true`, the ripple effect is disabled.
   *
   * ⚠️ Without a ripple there is no styling for :focus-visible by default. Be sure
   * to highlight the element by applying separate styles with the `.Mui-focusVisible` class.
   * @default false
   */
  disableRipple: PropTypes.bool,
  /**
   * Element placed after the children.
   */
  endIcon: PropTypes.node,
  /**
   * @ignore
   */
  focusVisibleClassName: PropTypes.string,
  /**
   * If `true`, the button will take up the full width of its container.
   * @default false
   */
  fullWidth: PropTypes.bool,
  /**
   * The URL to link to when the button is clicked.
   * If defined, an `a` element will be used as the root node.
   */
  href: PropTypes.string,
  /**
   * @ignore
   */
  id: PropTypes.string,
  /**
   * If `true`, the loading indicator is visible and the button is disabled.
   * If `true | false`, the loading wrapper is always rendered before the children to prevent [Google Translation Crash](https://github.com/mui/material-ui/issues/27853).
   * @default null
   */
  loading: PropTypes.bool,
  /**
   * Element placed before the children if the button is in loading state.
   * The node should contain an element with `role="progressbar"` with an accessible name.
   * By default, it renders a `CircularProgress` that is labeled by the button itself.
   * @default <CircularProgress color="inherit" size={16} />
   */
  loadingIndicator: PropTypes.node,
  /**
   * The loading indicator can be positioned on the start, end, or the center of the button.
   * @default 'center'
   */
  loadingPosition: PropTypes.oneOf(["center", "end", "start"]),
  /**
   * The size of the component.
   * `small` is equivalent to the dense button styling.
   * @default 'medium'
   */
  size: PropTypes /* @typescript-to-proptypes-ignore */.oneOfType([
    PropTypes.oneOf(["small", "medium", "large"]),
    PropTypes.string,
  ]),
  /**
   * Element placed before the children.
   */
  startIcon: PropTypes.node,
  /**
   * @ignore
   */
  style: PropTypes.object,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
    PropTypes.func,
    PropTypes.object,
  ]),
  /**
   * @ignore
   */
  type: PropTypes.string,
  /**
   * The variant to use.
   * @default 'text'
   */
  variant: PropTypes /* @typescript-to-proptypes-ignore */.oneOfType([
    PropTypes.oneOf(["contained", "outlined", "text"]),
    PropTypes.string,
  ]),
};

export default Button;
